import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import type { LibraryAdapter, ListQuery, LibraryItem } from "./library-adapter";
const stores = new WeakMap<
  LibraryAdapter,
  { version: number; listeners: Set<() => void> }
>();
function store(adapter: LibraryAdapter) {
  let s = stores.get(adapter);
  if (!s) {
    s = { version: 0, listeners: new Set() };
    stores.set(adapter, s);
  }
  return s;
}
export function invalidateLibrary(adapter: LibraryAdapter) {
  const s = store(adapter);
  s.version++;
  s.listeners.forEach((f) => f());
}
function useVersion(adapter: LibraryAdapter) {
  const s = store(adapter);
  return useSyncExternalStore(
    (f) => {
      s.listeners.add(f);
      return () => {
        s.listeners.delete(f);
      };
    },
    () => s.version,
    () => 0,
  );
}
export const libraryError = (e: unknown) =>
  e instanceof Error ? e.message : String(e);
/** key must contain every argument of load. Stale completions never replace a newer view. */
export function useLibraryResource<T>(
  adapter: LibraryAdapter,
  key: string | null,
  load: () => Promise<T>,
) {
  const version = useVersion(adapter),
    [retry, setRetry] = useState(0);
  const [state, setState] = useState<{
    adapter: LibraryAdapter | null;
    key: string | null;
    version: number;
    retry: number;
    data: T | null;
    error: string;
  }>({
    adapter: null,
    key: null,
    version: -1,
    retry: -1,
    data: null,
    error: "",
  });
  useEffect(() => {
    if (key === null) return;
    let live = true;
    void Promise.resolve()
      .then(load)
      .then((data) => {
        if (live) setState({ adapter, key, version, retry, data, error: "" });
      })
      .catch((e) => {
        if (live)
          setState({
            adapter,
            key,
            version,
            retry,
            data: null,
            error: libraryError(e),
          });
      });
    return () => {
      live = false;
    };
    // The query key includes every request argument. A new closure alone must not refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adapter, key, version, retry]);
  const current =
    state.adapter === adapter &&
    state.key === key &&
    state.version === version &&
    state.retry === retry;
  return {
    data: current ? state.data : null,
    error: current ? state.error : "",
    loading: key !== null && !current,
    retry: () => setRetry((v) => v + 1),
  };
}

export function useLibraryList(
  adapter: LibraryAdapter,
  query: Omit<ListQuery, "offset" | "limit">,
  limit = 50,
) {
  const version = useVersion(adapter),
    key = JSON.stringify(query),
    [retry, setRetry] = useState(0),
    epoch = useRef(0),
    pages = useRef({ key: "", count: 1 }),
    inFlight = useRef(false);
  const [state, setState] = useState<{
    adapter: LibraryAdapter | null;
    key: string;
    items: LibraryItem[];
    total: number;
    loading: boolean;
    error: string;
  }>({
    adapter: null,
    key: "",
    items: [],
    total: 0,
    loading: false,
    error: "",
  });
  useEffect(() => {
    const token = ++epoch.current;
    if (pages.current.key !== key) pages.current = { key, count: 1 };
    inFlight.current = true;
    setState((s) => ({
      adapter,
      key,
      items: s.key === key && s.adapter === adapter ? s.items : [],
      total: s.key === key ? s.total : 0,
      loading: true,
      error: "",
    }));
    void (async () => {
      try {
        const items: LibraryItem[] = [];
        let total = 0;
        for (let page = 0; page < pages.current.count; page++) {
          const r = await adapter.list({
            ...JSON.parse(key),
            offset: page * limit,
            limit,
          });
          items.push(...r.items);
          total = r.total;
          if (items.length >= total || !r.items.length) break;
        }
        if (token === epoch.current)
          setState({ adapter, key, items, total, loading: false, error: "" });
      } catch (e) {
        if (token === epoch.current)
          setState((s) => ({ ...s, loading: false, error: libraryError(e) }));
      } finally {
        if (token === epoch.current) inFlight.current = false;
      }
    })();
    return () => {
      if (epoch.current === token) epoch.current = token + 1;
    };
  }, [adapter, key, version, retry, limit]);
  const loadMore = async () => {
    if (
      inFlight.current ||
      state.key !== key ||
      state.items.length >= state.total
    )
      return;
    inFlight.current = true;
    const token = epoch.current;
    setState((s) => ({ ...s, loading: true, error: "" }));
    try {
      const r = await adapter.list({
        ...query,
        offset: state.items.length,
        limit,
      });
      if (token === epoch.current) {
        pages.current.count++;
        setState((s) => ({
          ...s,
          items: [...s.items, ...r.items],
          total: r.total,
          loading: false,
        }));
      }
    } catch (e) {
      if (token === epoch.current)
        setState((s) => ({ ...s, loading: false, error: libraryError(e) }));
    } finally {
      if (token === epoch.current) inFlight.current = false;
    }
  };
  return {
    ...(state.key === key && state.adapter === adapter
      ? state
      : { items: [], total: 0, loading: true, error: "" }),
    loadMore,
    retry: () => setRetry((v) => v + 1),
  };
}
